/**
 * Package loader — walks the workspace, parses `package.archspace` and
 * `.arch` files, recursively resolves declared dependencies, detects cycles,
 * and produces a `ResolvedPackage` ready for the LSP / CLI to consume.
 *
 * All filesystem access goes through `LspIO`, so the loader is identical
 * across node (real fs) and the browser (in-memory VFS). The pure
 * resolver in `@archlang/engine` operates on already-parsed inputs.
 */

import type { LspIO } from "../io.js";
import { parseCached, parsePackageManifest } from "@archlang/engine";
import {
  resolvePackage,
  type Diagnostic,
  type PackageInput,
  type PackageManifest,
  type PackageMap,
  type ParsedFile,
  type QualifiedName,
  type ResolvedPackage,
  type SpaceScopedUses,
} from "@archlang/engine";

export interface LoadedPackage {
  readonly resolved: ResolvedPackage;
  /** All files included in this package, with original URI for diagnostics. */
  readonly files: ReadonlyMap<string, ParsedFile>;
  /**
   * Raw source text for every file in `files`, keyed by the same URI.
   * Retained so consumers (the server's API surface, future inspectors)
   * can surface the original source without a second filesystem read.
   * Mirrors `files` exactly — every key in `files` is a key here.
   */
  readonly sources: ReadonlyMap<string, string>;
  /** Resolved root URI of this package. */
  readonly rootUri: string;
  /** Manifest, if a package.archspace existed. */
  readonly manifest?: PackageManifest;
  /**
   * URI of this package's widgets script, if the manifest declares one
   * and the file exists. Undefined when the manifest has no `widgets:`
   * field, or when the declared path doesn't resolve.
   */
  readonly widgetsUri?: string;
  /** Recursively resolved dependencies. */
  readonly dependencies: ReadonlyMap<string, LoadedPackage>;
}

export interface LoadOptions {
  /**
   * Override std lib URI. Defaults to whatever the supplied LspIO
   * exposes via `defaultStdlibUri()` (e.g., ARCHLANG_STDLIB env var
   * or the bundled stdlib under node). When neither is available the
   * loader emits STDLIB_NOT_FOUND on the first `arch.*` reference.
   */
  readonly stdlibPath?: string;
}

/** Discovery half of a package load: roots, dependency packages (resolved cold —
 *  dependents need their `exports`, which the engine's `state()` doesn't emit),
 *  file sources, space map, widgets URI — everything
 *  {@link IncrementalPackageCompiler.load} needs WITHOUT resolving the package
 *  itself. The incremental compiler owns that resolve. */
export interface DiscoveredPackage {
  readonly input: PackageInput;
  /** Resolved dependency packages, name → ResolvedPackage. */
  readonly deps: PackageMap;
  readonly files: ReadonlyMap<string, ParsedFile>;
  readonly sources: ReadonlyMap<string, string>;
  readonly rootUri: string;
  readonly manifest?: PackageManifest;
  readonly widgetsUri?: string;
  readonly dependencies: ReadonlyMap<string, LoadedPackage>;
  /** Discovery diagnostics (manifest/dependency/stdlib/cycle/parse). */
  readonly diagnostics: readonly Diagnostic[];
}

interface LoadContext {
  /** Resolved std lib root URI (may be a placeholder if unresolved). */
  readonly stdlibUri: string;
  /** Stack of package URIs currently being loaded (for cycle detection). */
  readonly resolving: Set<string>;
  /** Cache: rootUri → already-loaded package. Avoids redundant work and
   *  makes the diamond case (A → B, A → C, both → D) work as one D load. */
  readonly cache: Map<string, LoadedPackage>;
}

/**
 * Load and resolve a package starting from a directory URI.
 *
 * - If `<dir>/package.archspace` exists, parses it for name + dependencies.
 * - All `.arch` files under `<dir>` (recursively, stopping at any deeper
 *   `package.archspace` boundary) belong to this package.
 * - Dependencies are loaded recursively. `arch.*` names resolve against
 *   the std lib URI.
 */
export async function loadPackage(
  io: LspIO,
  rootUri: string,
  options: LoadOptions = {}
): Promise<LoadedPackage> {
  const stdlibUri =
    options.stdlibPath ??
    (await io.defaultStdlibUri?.()) ??
    "file:///__archlang_stdlib_unset__";
  const ctx: LoadContext = {
    stdlibUri,
    resolving: new Set(),
    cache: new Map(),
  };
  const single = await singleArchFile(io, rootUri);
  if (single) {
    const d = await discoverCore(io, single.dir, ctx, single.file);
    const resolved = resolvePackage(d.input, d.deps);
    return {
      resolved: { ...resolved, diagnostics: [...d.diagnostics, ...resolved.diagnostics] },
      files: d.files,
      sources: d.sources,
      rootUri: single.dir,
      manifest: d.manifest,
      widgetsUri: d.widgetsUri,
      dependencies: d.dependencies,
    };
  }
  return loadPackageInternal(io, normalizeDirUri(rootUri), ctx);
}

/** Discovery only (deps resolved cold, package itself NOT resolved) — feeds the
 *  incremental compiler. The product compile path uses this + an
 *  `IncrementalPackageCompiler`; the cold `loadPackage` stays for dependency
 *  resolution (needs `exports`), one-shot CLI/diff builds, and the test oracle. */
export async function discoverPackage(
  io: LspIO,
  rootUri: string,
  options: LoadOptions = {}
): Promise<DiscoveredPackage> {
  const stdlibUri =
    options.stdlibPath ??
    (await io.defaultStdlibUri?.()) ??
    "file:///__archlang_stdlib_unset__";
  const ctx: LoadContext = { stdlibUri, resolving: new Set(), cache: new Map() };
  const single = await singleArchFile(io, rootUri);
  if (single) return discoverCore(io, single.dir, ctx, single.file);
  return discoverCore(io, normalizeDirUri(rootUri), ctx);
}

/**
 * A direct `.arch` file path loads as a one-file **anonymous package** — the
 * lone file behaves exactly as if it sat alone in an empty directory
 * (BEST-PRACTICES: single-file drafts are first-class). Returns the file URI
 * plus its parent directory (the package root), or `undefined` when `rootUri`
 * is a directory or not an existing `.arch` file.
 */
async function singleArchFile(
  io: LspIO,
  rootUri: string
): Promise<{ file: string; dir: string } | undefined> {
  const stripped = stripTrailingSlash(rootUri);
  if (!stripped.endsWith(".arch")) return undefined;
  if (await io.isDirectory(stripped)) return undefined;
  if (!(await io.exists(stripped))) return undefined;
  return { file: stripped, dir: normalizeDirUri(io.dirname(stripped)) };
}

/** Full cold load = discovery + cold `resolvePackage`. Used for dependency
 *  packages (which must carry `exports`) and as the equivalence-test oracle /
 *  one-shot builds. */
async function loadPackageInternal(
  io: LspIO,
  rootUri: string,
  ctx: LoadContext
): Promise<LoadedPackage> {
  const cached = ctx.cache.get(rootUri);
  if (cached) return cached;
  const d = await discoverCore(io, rootUri, ctx);
  const resolved = resolvePackage(d.input, d.deps);
  const loaded: LoadedPackage = {
    resolved: { ...resolved, diagnostics: [...d.diagnostics, ...resolved.diagnostics] },
    files: d.files,
    sources: d.sources,
    rootUri,
    manifest: d.manifest,
    widgetsUri: d.widgetsUri,
    dependencies: d.dependencies,
  };
  ctx.cache.set(rootUri, loaded);
  return loaded;
}

/** Discovery: manifest, recursive dependency load (deps ARE resolved cold),
 *  stdlib auto-load, `.arch` collection + parse, space map, widgets URI. Stops
 *  short of resolving the package itself. */
async function discoverCore(
  io: LspIO,
  rootUri: string,
  ctx: LoadContext,
  /** When set, discover ONLY this one `.arch` file as an anonymous package
   *  (single-file draft). `rootUri` is then the file's parent directory. */
  singleFile?: string
): Promise<DiscoveredPackage> {
  // Verify the root directory actually exists. If not, callers (std lib
  // resolver, dep resolver) treat the throw as "not found" and emit a
  // proper diagnostic.
  if (!(await io.isDirectory(rootUri))) {
    throw new Error(`Package directory not found: ${rootUri}`);
  }

  const diagnostics: Diagnostic[] = [];

  // A single-file draft is an anonymous package: skip the manifest read (and,
  // below, the directory walk) so the lone file behaves exactly as if it sat
  // alone in an empty directory.
  let manifest: PackageManifest | undefined;
  if (!singleFile) {
    const manifestUri = io.joinUri(rootUri, "package.archspace");
    // Try to read the manifest. Missing manifest = anonymous package.
    const manifestText = await io.readFile(manifestUri);
    if (manifestText !== null) {
      const result = parsePackageManifest(manifestText, manifestUri);
      diagnostics.push(
        ...result.errors.map((e) => ({
          severity: "error" as const,
          message: e.message,
          source: e.span,
          code: "MANIFEST_PARSE_ERROR" as const,
        }))
      );
      manifest = result.manifest;
    }
  }

  // Reserve the `arch.*` namespace for the bundled standard library. A
  // user package living outside the stdlib root cannot claim that prefix.
  if (manifest && manifest.package?.[0] === "arch") {
    const insideStdlib = isInside(rootUri, ctx.stdlibUri);
    if (!insideStdlib) {
      diagnostics.push({
        severity: "error",
        message: `Package name '${manifest.package.join(".")}' uses the reserved 'arch.*' prefix. That prefix is reserved for the standard library.`,
        source: manifest.packageSource ?? manifest.source,
        code: "RESERVED_PACKAGE_NAMESPACE",
      });
    }
  }

  // Cycle detection. We mark this rootUri as resolving before loading
  // dependencies; any recursive call that lands here gets an error.
  if (ctx.resolving.has(rootUri)) {
    diagnostics.push({
      severity: "error",
      message: `Dependency cycle detected at ${rootUri}`,
      source: manifest?.source,
      code: "DEP_CYCLE",
    });
    // Return an empty placeholder so callers can keep going.
    return makeEmptyDiscovered(rootUri, manifest, diagnostics);
  }
  ctx.resolving.add(rootUri);

  // Load dependencies first.
  const depMap = new Map<string, LoadedPackage>();
  for (const dep of manifest?.dependencies ?? []) {
    const depUri = normalizeDirUri(io.joinUri(rootUri, dep.path));
    // If the dep is currently being resolved upstream of us, we'd close a
    // cycle. Emit the diagnostic here so the user-loaded package surfaces
    // it directly — without this check, the cycle would only land in the
    // empty placeholder buried in the recursion tree.
    if (ctx.resolving.has(depUri)) {
      diagnostics.push({
        severity: "error",
        message: `Dependency cycle: '${dep.name.join(".")}' depends back on this package`,
        source: dep.source,
        code: "DEP_CYCLE",
      });
      continue;
    }
    try {
      const loaded = await loadPackageInternal(io, depUri, ctx);
      depMap.set(dep.name.join("."), loaded);
      // Verify name matches what the depender claimed.
      const declaredName = loaded.manifest?.package?.join(".");
      const expectedName = dep.name.join(".");
      if (declaredName && declaredName !== expectedName) {
        diagnostics.push({
          severity: "error",
          message: `Dependency name mismatch: ${expectedName} expected, found '${declaredName}' at ${depUri}`,
          source: dep.nameSource ?? dep.source,
          code: "DEP_NAME_MISMATCH",
        });
      }
      // Propagate transitive cycles so any package along the cycle surfaces
      // a diagnostic, not just the recursive call site that detected it.
      if (loaded.resolved.diagnostics.some((d) => d.code === "DEP_CYCLE")) {
        diagnostics.push({
          severity: "error",
          message: `Dependency cycle reaches this package via '${dep.name.join(".")}'`,
          source: dep.source,
          code: "DEP_CYCLE",
        });
      }
    } catch (err) {
      diagnostics.push({
        severity: "error",
        message: `Failed to load dependency '${dep.name.join(".")}' at ${depUri}: ${String(err)}`,
        source: dep.pathSource ?? dep.source,
        code: "DEP_LOAD_FAILED",
      });
    }
  }

  // Std lib resolution: any `use ... from arch.*` referenced in the package
  // is auto-loaded if not already a declared dep.
  const stdlibUses = collectReferencedStdlib(manifest, []);
  for (const stdName of stdlibUses) {
    if (depMap.has(stdName)) continue;
    const stdUri = normalizeDirUri(io.joinUri(ctx.stdlibUri, stdName));
    try {
      const loaded = await loadPackageInternal(io, stdUri, ctx);
      depMap.set(stdName, loaded);
    } catch {
      diagnostics.push({
        severity: "error",
        message: `Std lib package '${stdName}' not found at ${stdUri}; check toolchain installation`,
        code: "STDLIB_NOT_FOUND",
      });
    }
  }

  // Walk .arch files under rootUri: descend into nested spaces (name:),
  // stop at nested packages (package:). A single-file draft is just that one
  // file (no walk, no enclosing space).
  const { files: archFiles, spaces: fileSpaces, spaceUses } = singleFile
    ? { files: [singleFile], spaces: new Map<string, QualifiedName>(), spaceUses: [] as SpaceScopedUses[] }
    : await collectArchFiles(io, rootUri);
  const files = new Map<string, ParsedFile>();
  const sources = new Map<string, string>();
  for (const fileUri of archFiles) {
    const text = await io.readFile(fileUri);
    if (text === null) {
      diagnostics.push({
        severity: "error",
        message: `Failed to read ${fileUri}`,
        code: "FILE_READ_ERROR",
      });
      continue;
    }
    try {
      const r = parseCached(text, fileUri);
      // A `.arch` file the parser can't make sense of (≥3 "Unexpected
      // character" *or* "Unexpected token at top level" errors) is almost
      // certainly another tool's format that merely shares the extension
      // (drawio XML, mermaid, a DSL, …). The parser's error recovery
      // fabricates phantom declarations from such garbage, so a
      // zero-declaration check doesn't work — but these two error classes are
      // a clean signal: genuine ArchLang files emit *positional* parser
      // ("Expected …") errors for typos, never a run of top-level garbage.
      // (Lexical alone is no longer enough: the v0.10 selector lexer tokenises
      // `<`, so XML angle-brackets now surface as top-level tokens, not
      // lexical errors.) Skip the file with a single info note instead of
      // flooding the model with PARSE_ERRORs; real authoring mistakes are
      // unaffected.
      // Match by catalog key, not message text — the text is now localised, so
      // a message-substring check would break under RU and drift with wording.
      const foreignErrors = r.errors.filter(
        (e) =>
          e.msgKey === "parse.unexpectedChar" ||
          (e.msgKey === "parse.unexpectedTokenIn" && e.params?.context === "top level")
      ).length;
      if (foreignErrors >= 3) {
        diagnostics.push({
          severity: "info",
          message: `${fileUri} is not an ArchLang file; skipped`,
          code: "NOT_ARCHLANG",
        });
        continue;
      }
      files.set(fileUri, r.file);
      sources.set(fileUri, text);
      diagnostics.push(
        ...r.errors.map((e) => ({
          severity: "error" as const,
          message: e.message,
          source: e.span,
          code: "PARSE_ERROR" as const,
        }))
      );

      // Resolve `use ... from arch.*` references discovered in this file.
      // `use policy <Name> from arch.*` counts: a governance package ships no
      // types, so nothing else in the file would ever pull it in, and an
      // unloaded home package makes every imported policy silently unresolvable.
      const fileRefs = [
        ...r.file.uses,
        ...r.file.declarations.flatMap((d) =>
          d.kind === "usePolicy"
            ? [{ fromPackage: d.decl.fromPackage, fromSource: d.decl.fromSource, source: d.decl.source }]
            : []
        ),
      ];
      for (const u of fileRefs) {
        const fromName = u.fromPackage.join(".");
        if (!fromName.startsWith("arch.")) continue;
        if (depMap.has(fromName)) continue;
        const stdUri = normalizeDirUri(io.joinUri(ctx.stdlibUri, fromName));
        try {
          const loaded = await loadPackageInternal(io, stdUri, ctx);
          depMap.set(fromName, loaded);
        } catch {
          diagnostics.push({
            severity: "error",
            message: `Std lib package '${fromName}' not found at ${stdUri}`,
            source: u.fromSource ?? u.source,
            code: "STDLIB_NOT_FOUND",
          });
        }
      }
    } catch (err) {
      diagnostics.push({
        severity: "error",
        message: `Failed to parse ${fileUri}: ${String(err)}`,
        code: "FILE_READ_ERROR",
      });
    }
  }

  // Auto-load stdlib packages referenced only from a nested space manifest's
  // `use` (the per-file / root-manifest passes above never see those).
  for (const su of spaceUses) {
    for (const u of su.uses) {
      const fromName = u.fromPackage.join(".");
      if (!fromName.startsWith("arch.")) continue;
      if (depMap.has(fromName)) continue;
      const stdUri = normalizeDirUri(io.joinUri(ctx.stdlibUri, fromName));
      try {
        depMap.set(fromName, await loadPackageInternal(io, stdUri, ctx));
      } catch {
        diagnostics.push({
          severity: "error",
          message: `Std lib package '${fromName}' not found at ${stdUri}`,
          source: u.fromSource ?? u.source,
          code: "STDLIB_NOT_FOUND",
        });
      }
    }
  }

  // Build PackageInput and resolve.
  const pkgInput: PackageInput = {
    name: manifest?.package,
    manifest,
    files: [...files.values()],
    rootPath: rootUri,
    spaces: fileSpaces,
    spaceUses,
  };
  const resolverDeps: PackageMap = new Map(
    [...depMap].map(([k, v]) => [k, v.resolved])
  );

  ctx.resolving.delete(rootUri);

  // Resolve the widgets script URI, if any. We verify the file exists so
  // a typo in the manifest surfaces a diagnostic at load time instead of
  // silently producing a missing-element error in the viewer.
  let widgetsUri: string | undefined;
  if (manifest?.widgets) {
    const candidate = io.joinUri(rootUri, manifest.widgets);
    if (await io.exists(candidate)) {
      widgetsUri = candidate;
    } else {
      diagnostics.push({
        severity: "error",
        message: `Widgets script '${manifest.widgets}' not found at ${candidate}`,
        source: manifest.widgetsSource ?? manifest.source,
        code: "WIDGETS_FILE_NOT_FOUND",
      });
    }
  }

  return {
    input: pkgInput,
    deps: resolverDeps,
    files,
    sources,
    rootUri,
    manifest,
    widgetsUri,
    dependencies: depMap,
    diagnostics,
  };
}

function makeEmptyDiscovered(
  rootUri: string,
  manifest: PackageManifest | undefined,
  diagnostics: readonly Diagnostic[]
): DiscoveredPackage {
  return {
    input: { manifest, files: [], rootPath: rootUri, name: manifest?.package },
    deps: new Map(),
    files: new Map(),
    sources: new Map(),
    rootUri,
    manifest,
    dependencies: new Map(),
    diagnostics,
  };
}

async function collectArchFiles(
  io: LspIO,
  rootUri: string
): Promise<{
  files: string[];
  spaces: Map<string, QualifiedName>;
  spaceUses: SpaceScopedUses[];
}> {
  const out: string[] = [];
  // file URI → enclosing space name (nearest ancestor `name:` manifest).
  const spaces = new Map<string, QualifiedName>();
  // Space-scoped imports (§4.11): a nested `name:` manifest's `use` is visible
  // to that space's whole subtree. Collected so the resolver can scope it.
  const spaceUses: SpaceScopedUses[] = [];
  const stack: { dirUri: string; space: QualifiedName | undefined }[] = [
    { dirUri: rootUri, space: undefined },
  ];
  while (stack.length > 0) {
    const { dirUri, space } = stack.pop()!;
    let entries: readonly { name: string; kind: "file" | "dir" }[];
    try {
      entries = await io.readDirectory(dirUri);
    } catch {
      continue;
    }
    let currentSpace = space;
    // A non-root package.archspace is either a nested package (opaque —
    // stop the walk) or a space (transparent — keep walking, tagging files
    // with the space name).
    if (
      dirUri !== rootUri &&
      entries.some((e) => e.name === "package.archspace" && e.kind === "file")
    ) {
      const mUri = io.joinUri(dirUri, "package.archspace");
      const text = await io.readFile(mUri);
      const m =
        text !== null ? parsePackageManifest(text, mUri).manifest : undefined;
      if (m?.package !== undefined) {
        continue; // nested package — opaque boundary, load separately.
      }
      if (m?.spaceName !== undefined) {
        currentSpace = m.spaceName; // enter the space.
        if (m.uses.length > 0) spaceUses.push({ space: m.spaceName, uses: m.uses });
      }
    }
    for (const entry of entries) {
      if (
        entry.name === "node_modules" ||
        entry.name === ".git" ||
        entry.name === "dist"
      ) {
        continue;
      }
      const childUri = io.joinUri(
        dirUri,
        entry.kind === "dir" ? entry.name + "/" : entry.name
      );
      if (entry.kind === "dir") {
        stack.push({ dirUri: normalizeDirUri(childUri), space: currentSpace });
      } else if (entry.name.endsWith(".arch")) {
        out.push(childUri);
        if (currentSpace !== undefined) spaces.set(childUri, currentSpace);
      }
    }
  }
  return { files: out, spaces, spaceUses };
}

function collectReferencedStdlib(
  manifest: PackageManifest | undefined,
  files: readonly ParsedFile[]
): string[] {
  const out = new Set<string>();
  const visit = (uses: readonly { fromPackage: QualifiedName }[]): void => {
    for (const u of uses) {
      const name = u.fromPackage.join(".");
      if (name.startsWith("arch.")) out.add(name);
    }
  };
  if (manifest) {
    visit(manifest.uses);
    // An adopted governance pack (`policies { arch.…: "<v>" }`) is a stdlib
    // reference too — the pack gate can't check a pack it never loaded.
    for (const p of manifest.policyPacks) {
      const name = p.name.join(".");
      if (name.startsWith("arch.")) out.add(name);
    }
  }
  for (const f of files) visit(f.uses);
  return [...out];
}

/**
 * URI-level "is `child` inside `parent`?" — used to verify a package
 * claiming the `arch.*` prefix actually lives under the stdlib root.
 * Works on opaque URI strings via prefix matching, so it's transport-
 * agnostic (file://, in-memory://, etc.).
 */
function isInside(child: string, parent: string): boolean {
  const c = stripTrailingSlash(child);
  const p = stripTrailingSlash(parent);
  return c === p || c.startsWith(p + "/");
}

function stripTrailingSlash(uri: string): string {
  return uri.endsWith("/") ? uri.slice(0, -1) : uri;
}

/**
 * Normalize a directory URI to a canonical no-trailing-slash form. The
 * loader keys its cache and cycle-detection set on this form so two
 * spellings of the same directory don't load it twice.
 */
function normalizeDirUri(uri: string): string {
  return stripTrailingSlash(uri);
}
